import { IconButton, StatusBadge, Tag } from '@components/ui';
import { TWeekStart } from '@components/ui/calendar/calendars';
import { getDateFormat } from '@helpers/utils-helpers';
import { CopyOutlined, DeleteOutlined, EditOutlined } from '@icon';
import getDaysArray from '@lib/utils/get-days-array';
import { addDays, startOfWeek } from 'date-fns';
import moment from 'moment';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  IOfficeHoursWeekRule,
  IOfficeHoursWeekRuleWithId,
  TDays,
  TOfficeHoursValidity,
} from 'types/e-visits';
import { getColorForStatus, OfficeHoursCS } from './office-hours-preview.styled';

type TItem = IOfficeHoursWeekRuleWithId | IOfficeHoursWeekRule;

interface OfficeHoursProps {
  officeHours?: TItem;
  style?: React.CSSProperties;
  weekStartsOn: TWeekStart;
  status: TOfficeHoursValidity;
  disabled?: boolean;

  onEditChange: (officeHours: IOfficeHoursWeekRuleWithId) => void;
  onDelete: (e: React.MouseEvent, officeHours: IOfficeHoursWeekRuleWithId) => void;
  onCopy: (officeHours: IOfficeHoursWeekRuleWithId) => void;

  canCopy?: boolean;
}

const OfficeHoursPreview: React.FunctionComponent<OfficeHoursProps> = ({
  officeHours,
  style,
  weekStartsOn,
  status,
  disabled,
  onEditChange,
  onDelete,
  onCopy,
  canCopy,
}) => {
  const weekStart = startOfWeek(new Date(), { weekStartsOn });
  const weekDays = getDaysArray<TDays>(weekStartsOn, 'number');

  return (
    <OfficeHoursCS style={style} status={status} disabled={disabled}>
      <div className="title">
        <span className="title--text"></span>
        {disabled || !(officeHours && 'id' in officeHours) ? null : (
          <div className="title--office-actions">
            {canCopy && (
              <IconButton
                onClick={() => onCopy(officeHours)}
                Icon={CopyOutlined}
                size="medium"
                variant="ghost-secondary"
                shape="round"
              />
            )}
            {status === 'prepared' && (
              <IconButton
                onClick={() => {
                  onEditChange(officeHours);
                }}
                Icon={EditOutlined}
                size="medium"
                variant="ghost-secondary"
                shape="round"
              />
            )}
            {status !== 'inactive' && (
              <IconButton
                onClick={(e) => onDelete(e, officeHours)}
                Icon={DeleteOutlined}
                size="medium"
                variant="danger-outlined"
                shape="round"
              />
            )}
          </div>
        )}
      </div>
      <div className="content">
        <table>
          <tbody>
            {weekDays.map((d, index) => (
              <tr key={index} className="day-row">
                <th className="day">{moment(addDays(weekStart, index)).format('dd')}</th>
                <td style={{ width: '100%' }} className="days">
                  {officeHours?.days[d] && officeHours.days[d]!.length > 0 ? (
                    <div className="times">
                      {officeHours.days[d]?.map((hours, i2) => (
                        <Tag key={`${hours.from}-${hours.to}-${i2}`}>{`${moment(hours.from).format(
                          'HH:mm',
                        )} - ${moment(hours.to).format('HH:mm')}`}</Tag>
                      ))}
                    </div>
                  ) : (
                    <div>-</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="footer">
        {officeHours?.validFrom ? (
          <StatusBadge
            colorLabel
            size="medium"
            variant={getColorForStatus(status)}
            label={
              <FormattedMessage
                id="office_hours_validFrom"
                values={{ date: moment(officeHours.validFrom).format(getDateFormat()) }}
              />
            }
          />
        ) : null}
        {officeHours?.validTo ? (
          <StatusBadge
            colorLabel
            size="medium"
            variant={getColorForStatus(status)}
            label={
              <FormattedMessage
                id="office_hours_validTo"
                values={{ date: moment(officeHours.validTo).format(getDateFormat()) }}
              />
            }
          />
        ) : null}
      </div>
    </OfficeHoursCS>
  );
};

export default React.memo<OfficeHoursProps>(OfficeHoursPreview);
